import { z } from "zod";

const shortFeatures = z.array(
  z.object({
    description: z.object({
      pl: z.string(),
    }),
    icon: z.string(),
    name: z.string(),
    priority: z.number(),
  }),
);

const commonOffer = z.object({
  offerId: z.string(),
  state: z.string(),
  display: z.boolean().optional(),
  priority: z.number(),
  shortFeatures,
  longFeatures: z.array(
    z.object({
      title: z.object({
        pl: z.string(),
      }),
      description: z.object({
        pl: z.string(),
      }),
      icon: z.string(),
      name: z.string(),
      priority: z.number(),
    }),
  ),
  cardFeatures: z.array(
    z.object({
      description: z.object({
        pl: z.string(),
      }),
      additionalDescription: z
        .object({
          pl: z.string(),
        })
        .optional(),
      icon: z.string(),
      name: z.string(),
      priority: z.number(),
    }),
  ),
  extraShortFeatures: z
    .array(
      z.object({
        description: z.object({
          pl: z.string(),
        }),
        icon: z.string(),
        name: z.string(),
        priority: z.number(),
      }),
    )
    .optional(),
  discountFeatures: z
    .array(
      z.object({
        description: z.object({
          pl: z.string(),
        }),
        icon: z.string().optional(),
        name: z.string(),
        priority: z.number(),
        additionalDescription: z
          .object({
            pl: z.string(),
          })
          .optional(),
      }),
    )
    .optional(),
  bonusFeatures: z
    .array(
      z.object({
        description: z.object({
          pl: z.string(),
        }),
        icon: z.string().optional(),
        name: z.string(),
        priority: z.number(),
        additionalDescription: z
          .object({
            pl: z.string(),
          })
          .optional(),
      }),
    )
    .optional(),
  paymentFeatures: z
    .array(
      z.object({
        description: z.object({
          pl: z.string(),
        }),
        icon: z.string(),
        name: z.string(),
        priority: z.number(),
      }),
    )
    .optional(),
  label: z.string(),
  totalRecurringPrice: z.number(),
  totalOneTimePrice: z.number(),
  totalPrice: z.number(),
  isMvpVersion: z.boolean().optional(),
  isFeatured: z.boolean().optional(),
  ftthSfhPrice: z.number().optional(),
  activationFee: z.number().optional(),
  activationFeeWithConsentsDiscount: z.number().optional(),
});

//#region FTTH offers
const ftthOffer = commonOffer.extend({
  category: z.literal("FTTH"),
  uploadSpeed: z.number(),
  downloadSpeed: z.number(),
  downloadFullText: z.string().optional(),
  labels: z.array(z.string()).optional(),
  uploadFullText: z.string().optional(),
  limited: z.boolean(),
  bundleDiscount: z.number().optional(),
  ftthBundleDiscount: z.number().optional(),
  mobileMultiLineDiscount: z.number().optional(),
  priceDiscountedAfterMobile: z.number().optional(),
  priceDiscountedAfterFtth: z.number().optional(),
  priceDiscountedAfterMobileFtth: z.number().optional(),
  monthlyFeeInHouse: z.number().optional(),
  penaltyFeeModemReturn: z.number().optional(),
});

const loyalty = z.object({
  offerId: z.string(),
  paymentsAmount: z.number(),
  commitment: z.literal(true),
  penaltyFee: z.number(),
});

const indefinite = z.object({
  offerId: z.string(),
  commitment: z.literal(false),
});

export const fSecureOffer = z.object({
  offerId: z.string(),
  category: z.literal("FSECURE"),
  state: z.string(),
  priority: z.number(),
  display: z.boolean(),
  shortFeatures,
  longFeatures: z.array(
    z.object({
      name: z.string(),
      priority: z.number(),
      description: z.object({
        pl: z.string(),
      }),
      icon: z.string().optional(),
    }),
  ),
  offerSelectionFeatures: z.array(
    z.object({
      description: z.object({
        pl: z.string(),
      }),
      icon: z.string(),
      name: z.string(),
      priority: z.number(),
    }),
  ),
  activeServiceFeatures: z.array(
    z.object({
      description: z.object({
        pl: z.string(),
      }),
    }),
  ),
  displayName: z.array(
    z.object({
      additionalDescription: z
        .object({
          pl: z.string(),
        })
        .optional(),
    }),
  ),
  totalRecurringPrice: z.number(),
  totalOneTimePrice: z.number(),
  totalPrice: z.number(),
  baseRecurringPrice: z.number(),
  vpnPrice: z.number(),
  licenseSize: z.number(),
  vpnIncluded: z.boolean(),
  activationFee: z.number().optional(),
  activationFeeWithConsentsDiscount: z.number().optional(),
});

export const loyaltyFtthOffer = loyalty.extend(ftthOffer.shape);
export const indefiniteFtthOffer = indefinite.extend(ftthOffer.shape);

export const ftthOffers = z.union([indefiniteFtthOffer, loyaltyFtthOffer]);
//#endregion

export const mobileOffer = commonOffer.extend({
  category: z.literal("MOBILE"),
  dataAllowance: z.number(),
  bundleDiscount: z.number().optional(),
  ftthBundleDiscount: z.number().optional(),
  mobileMultiLineDiscount: z.number().optional(),
  priceDiscountedAfterMobile: z.number().optional(),
  priceDiscountedAfterFtth: z.number().optional(),
  priceDiscountedAfterMobileFtth: z.number().optional(),
});

export const postpaidOffer = commonOffer.extend({
  category: z.literal("MOBILE_POSTPAID"),
  priceDiscountedAfterMobile: z.number().optional(),
});

export const serverResponse = z.array(
  ftthOffers.or(mobileOffer).or(fSecureOffer).or(postpaidOffer),
);
